/**
 * カメラを開けなかった理由を、客に伝えられる粒度に分類する (#2111)。
 *
 * **「カメラを起動できません」だけでは直せない。** 実際に当日「QR が読めない」と
 * 言われ、原因が許可の拒否だと本人も分からなかった。許可されていないのか、
 * 端末にカメラが無いのか、他のアプリが掴んでいるのかで、やることが全く違う。
 *
 * ブラウザによって投げるものが `DOMException` だったり文字列だったりするので
 * (html5-qrcode は reject に文字列を渡すことがある)、name と文面の両方を見る。
 */
export type CameraFailure = 'denied' | 'notFound' | 'inUse' | 'insecure' | 'unknown'

export function classifyCameraError(err: unknown): CameraFailure {
  // https でないと getUserMedia 自体が無い。許可の問題と区別が付かないので先に見る
  if (typeof window !== 'undefined' && window.isSecureContext === false) return 'insecure'

  const name = typeof err === 'object' && err !== null && 'name' in err ? String((err as { name: unknown }).name) : ''
  const message = err instanceof Error ? err.message : String(err ?? '')
  const text = `${name} ${message}`

  // 拒否。iOS Safari は SecurityError、ダイアログを閉じただけのときは dismissed
  if (/NotAllowedError|SecurityError|PermissionDeniedError|Permission denied|Permission dismissed|permission/i.test(text)) {
    return 'denied'
  }
  if (/NotFoundError|DevicesNotFoundError|OverconstrainedError|Requested device not found|no camera/i.test(text)) {
    return 'notFound'
  }
  // 他のアプリ (通話・撮影) が掴んでいる。Android でよく出る
  if (/NotReadableError|TrackStartError|Could not start video source|in use/i.test(text)) {
    return 'inUse'
  }
  return 'unknown'
}

/** 分類に対応する i18n キー (title / hint)。prefix は画面ごとに渡す。 */
export function cameraErrorKeys(failure: CameraFailure): { title: string; hint: string } {
  switch (failure) {
    case 'denied':
      return { title: 'scan.cameraDenied', hint: 'scan.cameraDeniedHint' }
    case 'notFound':
      return { title: 'scan.cameraNotFound', hint: 'scan.cameraNotFoundHint' }
    case 'inUse':
      return { title: 'scan.cameraInUse', hint: 'scan.cameraInUseHint' }
    case 'insecure':
      return { title: 'scan.cameraInsecure', hint: 'scan.cameraInsecureHint' }
    default:
      return { title: 'scan.cameraError', hint: 'scan.cameraUnknownHint' }
  }
}
